import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Palette, Building2, Upload, Trash2, Sun, Moon, Monitor, Save,
  ShoppingCart, CreditCard,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useSystemSettings } from "../lib/systemSettings";
import { useAuth } from "../lib/auth";
import { SystemMailPanel, MailHistoryPanel } from "../components/newsletter/panels";
import { Mail as MailIcon, History as HistoryIcon } from "lucide-react";

/**
 * Admin/Employee → Einstellungen.
 *
 * - Admin: sieht Branding + Darstellung (Firmenname, Logo, Theme-Default,
 *   drei globale Akzentfarben). Kundenportal-CMS (News, Standorte, Rechts-
 *   texte, Popups) liegt bewusst NICHT hier — das gehört in
 *   ``/kundenportal`` (AdminKundenportal).
 * - Employee: sieht nur den Tab „Darstellung", und dort nur die
 *   *persönliche* Theme-Auswahl (kein globaler Default, keine Akzentfarbe).
 *
 * Backend-RBAC bleibt maßgeblich: die Admin-Endpunkte sind serverseitig
 * über require_role("admin") gehärtet.
 */
export default function Einstellungen() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="space-y-6 w-full min-w-0 max-w-full" data-testid="einstellungen-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-cyan-400">
          {isAdmin ? "Admin" : "Mitarbeiter"}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">
          Einstellungen
        </h1>
        <div className="text-sm text-zinc-500 mt-1">
          {isAdmin ? "Globale Konfiguration für Branding und Darstellung."
                   : "Deine persönliche Darstellung."}
        </div>
      </div>

      {isAdmin ? (
        <Tabs defaultValue="branding" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 flex-wrap h-auto w-full sm:w-auto justify-start gap-1">
            <TabsTrigger value="branding" data-testid="settings-tab-branding" className="whitespace-nowrap">
              <Building2 size={14} className="mr-2 shrink-0" /> Unternehmensbranding
            </TabsTrigger>
            <TabsTrigger value="theme" data-testid="settings-tab-theme" className="whitespace-nowrap">
              <Palette size={14} className="mr-2 shrink-0" /> Darstellung
            </TabsTrigger>
            <TabsTrigger value="pos" data-testid="settings-tab-pos" className="whitespace-nowrap">
              <ShoppingCart size={14} className="mr-2 shrink-0" /> Kasse
            </TabsTrigger>
            <TabsTrigger value="system-mail" data-testid="settings-tab-system-mail" className="whitespace-nowrap">
              <MailIcon size={14} className="mr-2 shrink-0" /> System-Mails
            </TabsTrigger>
            <TabsTrigger value="mail-history" data-testid="settings-tab-mail-history" className="whitespace-nowrap">
              <HistoryIcon size={14} className="mr-2 shrink-0" /> Mail-Verlauf
            </TabsTrigger>
          </TabsList>
          <TabsContent value="branding" className="pt-4"><BrandingPanel /></TabsContent>
          <TabsContent value="theme" className="pt-4"><ThemePanel adminMode /></TabsContent>
          <TabsContent value="pos" className="pt-4"><PosPanel /></TabsContent>
          <TabsContent value="system-mail" className="pt-4"><SystemMailPanel /></TabsContent>
          <TabsContent value="mail-history" className="pt-4"><MailHistoryPanel /></TabsContent>
        </Tabs>
      ) : (
        <ThemePanel adminMode={false} />
      )}
    </div>
  );
}

function BrandingPanel() {
  const qc = useQueryClient();
  const { refresh } = useSystemSettings();
  const [companyName, setCompanyName] = useState("");
  const [uploading, setUploading] = useState(false);

  const q = useQuery({
    queryKey: ["admin-system-settings"],
    queryFn: async () => (await api.get("/admin/system-settings")).data,
  });

  useEffect(() => {
    if (q.data?.company_name != null) setCompanyName(q.data.company_name || "");
  }, [q.data]);

  const logoUrl = q.data?.logo_data_url || null;

  const saveName = useMutation({
    mutationFn: async () => (await api.put("/admin/system-settings/company_name", { value: companyName })).data,
    onSuccess: () => {
      toast.success("Firmenname gespeichert");
      qc.invalidateQueries({ queryKey: ["admin-system-settings"] });
      refresh();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Speichern fehlgeschlagen"),
  });

  async function handleLogoUpload(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/admin/system-settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo hochgeladen");
      qc.invalidateQueries({ queryKey: ["admin-system-settings"] });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally { setUploading(false); }
  }

  async function removeLogo() {
    if (!window.confirm("Aktuelles Logo entfernen und auf Text-Branding zurücksetzen?")) return;
    try {
      await api.delete("/admin/system-settings/logo");
      toast.success("Logo entfernt");
      qc.invalidateQueries({ queryKey: ["admin-system-settings"] });
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Entfernen fehlgeschlagen"); }
  }

  return (
    <div className="grid gap-6 sm:grid-cols-2 w-full min-w-0" data-testid="branding-panel">
      <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5">
        <div className="text-xs uppercase tracking-wider text-cyan-400 mb-3">Firmenname</div>
        <Label className="text-xs uppercase tracking-wider text-zinc-400">Anzeige im Header/Portal</Label>
        <Input data-testid="branding-company-name"
          value={companyName} onChange={(e) => setCompanyName(e.target.value)}
          maxLength={120}
          className="mt-1 h-11 bg-zinc-950 border-zinc-800 rounded-sm" />
        <div className="text-[11px] text-zinc-500 mt-2">
          Wird im gesamten UI (Header, Kundenportal, Belege, Popups) verwendet.
        </div>
        <div className="mt-4">
          <Button data-testid="branding-save-name" onClick={() => saveName.mutate()}
            className="bg-cyan-400 hover:bg-cyan-300">
            <Save size={14} className="mr-2" /> Speichern
          </Button>
        </div>
      </section>

      <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5">
        <div className="text-xs uppercase tracking-wider text-cyan-400 mb-3">Firmenlogo</div>
        <div className="flex items-center gap-4 mb-3">
          {logoUrl ? (
            <img data-testid="branding-logo-preview" src={logoUrl} alt=""
                 className="h-16 max-w-[180px] object-contain bg-zinc-950 border border-zinc-800 rounded-sm p-2" />
          ) : (
            <div className="h-16 w-40 border border-dashed border-zinc-700 rounded-sm flex items-center justify-center text-zinc-600 text-xs uppercase">
              Kein Logo
            </div>
          )}
        </div>
        <input id="branding-logo-file" data-testid="branding-logo-file" type="file"
               accept="image/png,image/jpeg,image/webp" className="hidden"
               onChange={(e) => handleLogoUpload(e.target.files?.[0])} />
        <div className="flex flex-wrap gap-2">
          <Button type="button" data-testid="branding-upload-btn"
            className="bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-cyan-400"
            onClick={() => document.getElementById("branding-logo-file").click()}
            disabled={uploading}>
            <Upload size={14} className="mr-2" /> {uploading ? "Lade hoch…" : (logoUrl ? "Ersetzen" : "Logo hochladen")}
          </Button>
          {logoUrl && (
            <Button type="button" variant="ghost" className="text-rose-400"
              data-testid="branding-remove-btn" onClick={removeLogo}>
              <Trash2 size={14} className="mr-2" /> Entfernen
            </Button>
          )}
        </div>
        <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
          PNG, JPG/JPEG, WebP · max. 3&nbsp;MB. Persistente Speicherung in Postgres.
        </div>
      </section>
    </div>
  );
}

function ThemePanel({ adminMode }) {
  const qc = useQueryClient();
  const { theme, setTheme, refresh, settings } = useSystemSettings();
  const [accent, setAccent] = useState(settings?.accent_color || "");
  const [accent2, setAccent2] = useState(settings?.accent_color_secondary || "");
  const [accent3, setAccent3] = useState(settings?.accent_color_interactive || "");
  useEffect(() => { setAccent(settings?.accent_color || ""); }, [settings?.accent_color]);
  useEffect(() => { setAccent2(settings?.accent_color_secondary || ""); }, [settings?.accent_color_secondary]);
  useEffect(() => { setAccent3(settings?.accent_color_interactive || ""); }, [settings?.accent_color_interactive]);

  const q = useQuery({
    queryKey: ["admin-system-settings"],
    queryFn: async () => (await api.get("/admin/system-settings")).data,
    enabled: !!adminMode,
  });
  const defaultTheme = q.data?.theme_default || "";

  const saveDefault = useMutation({
    mutationFn: async (t) => (await api.put("/admin/system-settings/theme_default", { value: t })).data,
    onSuccess: () => { toast.success("Standard-Theme gespeichert"); qc.invalidateQueries({ queryKey: ["admin-system-settings"] }); refresh(); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Speichern fehlgeschlagen"),
  });

  const useAccentSaver = (key, localVal, setLocalVal, savedKey) => useMutation({
    mutationFn: async () => (await api.put(`/admin/system-settings/${key}`, { value: localVal })).data,
    onSuccess: () => { toast.success("Akzentfarbe gespeichert"); qc.invalidateQueries({ queryKey: ["admin-system-settings"] }); refresh(); },
    onError: (e) => {
      const last = settings?.[savedKey] || "";
      setLocalVal(last);
      refresh();
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Ungültige Farbe");
    },
  });
  const saveAccent  = useAccentSaver("accent_color",           accent,  setAccent,  "accent_color");
  const saveAccent2 = useAccentSaver("accent_color_secondary", accent2, setAccent2, "accent_color_secondary");
  const saveAccent3 = useAccentSaver("accent_color_interactive", accent3, setAccent3, "accent_color_interactive");

  const options = [
    { value: "dark", label: "Dunkel", icon: Moon },
    { value: "light", label: "Hell", icon: Sun },
    { value: "system", label: "System", icon: Monitor },
  ];

  return (
    <div className="grid gap-6 sm:grid-cols-2 w-full min-w-0" data-testid="theme-panel">
      <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5">
        <div className="text-xs uppercase tracking-wider text-cyan-400 mb-3">Dein Theme</div>
        <div className="text-sm text-zinc-400 mb-3">Nur für diesen Login/Gerät.</div>
        <div className="grid grid-cols-3 gap-2">
          {options.map((o) => (
            <button key={o.value} type="button"
              data-testid={`theme-select-${o.value}`}
              onClick={() => setTheme(o.value)}
              className={`flex flex-col items-center gap-1 border rounded-sm py-3 transition-colors ${
                theme === o.value ? "border-cyan-400 bg-cyan-500/10 text-cyan-400" : "border-zinc-800 hover:border-zinc-600"
              }`}>
              <o.icon size={20} />
              <span className="text-xs uppercase tracking-wider">{o.label}</span>
            </button>
          ))}
        </div>
      </section>

      {adminMode && (
        <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5">
          <div className="text-xs uppercase tracking-wider text-cyan-400 mb-3">Standard-Theme (global)</div>
          <div className="text-sm text-zinc-400 mb-3">Wird für neue Sitzungen ohne lokale Präferenz verwendet.</div>
          <div className="grid grid-cols-3 gap-2">
            {options.map((o) => (
              <button key={o.value} type="button"
                data-testid={`theme-default-${o.value}`}
                onClick={() => saveDefault.mutate(o.value)}
                className={`flex flex-col items-center gap-1 border rounded-sm py-3 transition-colors ${
                  defaultTheme === o.value ? "border-cyan-400 bg-cyan-500/10 text-cyan-400" : "border-zinc-800 hover:border-zinc-600"
                }`}>
                <o.icon size={20} />
                <span className="text-xs uppercase tracking-wider">{o.label}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {adminMode && (
        <section className="sm:col-span-2 border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5 min-w-0">
          <div className="text-xs uppercase tracking-wider text-cyan-400 mb-3">Akzentfarben (global)</div>
          <div className="text-sm text-zinc-400 mb-4 leading-relaxed">
            Primär: Buttons, aktive Tabs, Links, Fokus. Sekundär: Highlights,
            Badges, Community-Akzente. Interaktion: Hover, Pressed und
            ausgewählte Elemente. Statusfarben (Fehler/Warnung/Erfolg) bleiben
            unabhängig.
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 min-w-0">
            <AccentRow
              label="Primäre Akzentfarbe"
              testIdPrefix="accent-color"
              value={accent} setValue={setAccent}
              save={saveAccent}
            />
            <AccentRow
              label="Sekundäre Akzentfarbe"
              testIdPrefix="accent-color-secondary"
              value={accent2} setValue={setAccent2}
              save={saveAccent2}
            />
            <AccentRow
              label="Interaktionsfarbe (Aktiv/Pressed)"
              testIdPrefix="accent-color-interactive"
              value={accent3} setValue={setAccent3}
              save={saveAccent3}
            />
          </div>
          <div className="text-[11px] text-zinc-500 mt-3">
            Textkontrast (schwarz/weiß) wird automatisch aus der Helligkeit abgeleitet.
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Admin/POS-Panel — Kundenkartenpflicht an der Kasse (Phase C).
 * Wechselt den Setting-Key ``pos_customer_card_required`` zwischen
 * ``voluntary`` und ``mandatory``. Backend erzwingt zusätzlich beim
 * Checkout (`services/pos_service.py`).
 */
function PosPanel() {
  const qc = useQueryClient();
  const { refresh, settings } = useSystemSettings();
  const q = useQuery({
    queryKey: ["admin-system-settings"],
    queryFn: async () => (await api.get("/admin/system-settings")).data,
  });
  const current = (q.data?.pos_customer_card_required
    || settings?.pos_customer_card_required
    || "voluntary").toLowerCase();

  const save = useMutation({
    mutationFn: async (v) =>
      (await api.put("/admin/system-settings/pos_customer_card_required", { value: v })).data,
    onSuccess: () => {
      toast.success("Kundenkarten-Einstellung gespeichert");
      qc.invalidateQueries({ queryKey: ["admin-system-settings"] });
      refresh();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Speichern fehlgeschlagen"),
  });

  const options = [
    {
      value: "voluntary",
      label: "Freiwillig",
      desc: "Kunden dürfen mit oder ohne Clubkarte einkaufen. Standardverhalten.",
    },
    {
      value: "mandatory",
      label: "Pflicht",
      desc: "POS ist gesperrt, bis eine gültige Clubkarte gescannt wurde. Backend blockiert Abschluss ohne Kundenzuordnung.",
    },
  ];

  return (
    <div className="grid gap-6 w-full min-w-0" data-testid="pos-panel">
      <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider text-cyan-400 flex items-center gap-2">
          <CreditCard size={14} /> Kundenkarte an der Kasse
        </div>
        <div className="text-sm text-zinc-400 leading-relaxed">
          Legt fest, ob an der POS-Kasse für jeden Verkauf eine Clubkarte
          benötigt wird. Die Einstellung wird global gespeichert und
          serverseitig erzwungen — sie kann nicht durch einen direkten
          API-Aufruf umgangen werden.
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {options.map((o) => {
            const active = current === o.value;
            return (
              <button
                key={o.value}
                type="button"
                data-testid={`pos-card-required-${o.value}`}
                onClick={() => save.mutate(o.value)}
                disabled={save.isPending}
                className={`text-left border rounded-sm p-4 transition-colors ${
                  active
                    ? "border-cyan-400 bg-cyan-500/10"
                    : "border-zinc-800 hover:border-zinc-600 bg-zinc-950/40"
                }`}
              >
                <div className={`text-sm font-bold uppercase tracking-wider mb-1 ${active ? "text-cyan-400" : "text-zinc-200"}`}>
                  {o.label}
                </div>
                <div className="text-xs text-zinc-400 leading-relaxed">
                  {o.desc}
                </div>
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-zinc-500 leading-relaxed">
          Änderungen greifen sofort für neue POS-Sitzungen. Laufende Kassensitzungen
          aktualisieren die Einstellung beim nächsten System-Settings-Refresh.
        </div>
      </section>
      <PosAdBannerSection />
    </div>
  );
}


/**
 * POS-Werbebanner-Verwaltung (Phase C.2/C.3).
 *
 * Verwaltet drei Slots (1, 2, 3). Für jeden Slot: Bild hochladen,
 * ersetzen, aktivieren/deaktivieren, entfernen — inklusive Vorschau
 * in EXAKT derselben Fläche wie im POS. Backend-seitig eigene
 * data_url- und active-Keys je Slot; Frontend zeigt bei ≥ 2 aktiven
 * Slots Rotation im POS.
 *
 * Optimales Bildformat: 1400 × 200 px (7:1). Größere/andere Ratios
 * werden per ``object-cover`` proportional beschnitten (nicht
 * verzerrt). Format-Limits identisch für alle Slots.
 */
function PosAdBannerSection() {
  const qc = useQueryClient();
  const { refresh, settings } = useSystemSettings();
  const q = useQuery({
    queryKey: ["admin-system-settings"],
    queryFn: async () => (await api.get("/admin/system-settings")).data,
  });
  const data = q.data || {};
  const slots = [
    { n: 1, urlKey: "pos_ad_banner_data_url",   activeKey: "pos_ad_banner_active" },
    { n: 2, urlKey: "pos_ad_banner_2_data_url", activeKey: "pos_ad_banner_2_active" },
    { n: 3, urlKey: "pos_ad_banner_3_data_url", activeKey: "pos_ad_banner_3_active" },
  ];

  return (
    <section
      className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5 space-y-4"
      data-testid="pos-ad-banner-panel"
    >
      <div className="text-xs uppercase tracking-wider text-cyan-400 flex items-center gap-2">
        <ShoppingCart size={14} /> POS Werbebanner
      </div>
      <div className="text-sm text-zinc-400 leading-relaxed">
        Bis zu 3 Werbebanner-Slots. Wenn mehrere aktiv sind, wechseln sie
        an der Kasse alle 9&nbsp;Sekunden mit sanftem Fade. Bei genau
        einem aktiven Slot bleibt das Motiv statisch. Ohne aktive Slots
        wird gar keine Bannerfläche im POS gerendert (kein leerer Rahmen,
        kein Platzhalter).
      </div>
      <div className="text-[11px] text-zinc-500 leading-relaxed">
        Optimale Bildgröße: <span className="text-zinc-300 font-mono">1400 × 200 px (7:1)</span>.
        Größere oder andere Formate werden proportional beschnitten
        (kein Verzerren). PNG, JPG oder WebP · max. 1.5&nbsp;MB je Slot.
      </div>

      <div className="grid gap-3">
        {slots.map((s) => (
          <PosAdBannerSlot
            key={s.n}
            slot={s.n}
            url={data[s.urlKey] || settings?.[s.urlKey] || ""}
            active={((data[s.activeKey] ?? settings?.[s.activeKey] ?? "") + "").trim() === "1"}
            activeKey={s.activeKey}
            onChanged={() => { qc.invalidateQueries({ queryKey: ["admin-system-settings"] }); refresh(); }}
          />
        ))}
      </div>
    </section>
  );
}


function PosAdBannerSlot({ slot, url, active, activeKey, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const { theme } = useSystemSettings();
  // Light-Theme-Detection reagiert auf ``system`` via matchMedia. Bewusst
  // KEIN Live-Listener auf Media-Query — der Benutzer setzt das Theme im
  // Header explizit, ein zusätzliches Re-Render wäre reine Politur.
  const isLight =
    theme === "light" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-color-scheme: light)").matches);
  const inputId = `pos-ad-banner-file-${slot}`;

  async function handleUpload(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/admin/system-settings/pos-ad-banner/${slot}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Banner ${slot} hochgeladen und aktiviert`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally { setUploading(false); }
  }

  async function removeBanner() {
    if (!window.confirm(`Banner ${slot} entfernen?`)) return;
    try {
      await api.delete(`/admin/system-settings/pos-ad-banner/${slot}`);
      toast.success(`Banner ${slot} entfernt`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Entfernen fehlgeschlagen");
    }
  }

  async function toggleActive(next) {
    try {
      await api.put(`/admin/system-settings/${activeKey}`, { value: next ? "1" : "" });
      toast.success(next ? `Banner ${slot} aktiviert` : `Banner ${slot} deaktiviert`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  }

  // Shared: verstecktes File-Input + gemeinsame Drag&Drop-Handler.
  // (Nur im Empty-State aktiviert — im Preview-State bleibt Klick-Upload.)
  const fileInput = (
    <input
      id={inputId}
      data-testid={`pos-ad-banner-file-${slot}`}
      type="file"
      accept="image/png,image/jpeg,image/webp"
      className="hidden"
      onChange={(e) => handleUpload(e.target.files?.[0])}
    />
  );

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleUpload(f);
  }
  function onDragOver(e) { e.preventDefault(); setDragOver(true); }
  function onDragLeave() { setDragOver(false); }

  // -------------------------------------------------------------------
  // LIGHT THEME — bewusst als separater Render-Pfad. Der Dark-Theme-Zweig
  // darunter bleibt 100 % identisch zum vorherigen Stand (Feature Freeze).
  // -------------------------------------------------------------------
  if (isLight) {
    return (
      <div
        className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-5 shadow-sm space-y-4 transition-shadow hover:shadow-md"
        data-testid={`pos-ad-banner-slot-${slot}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-cyan-100 text-cyan-700 font-black text-xs">
              {slot}
            </div>
            <div className="text-sm font-semibold text-zinc-900">Banner {slot}</div>
          </div>
          <div
            data-testid={`pos-ad-banner-status-${slot}`}
            className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${
              url && active
                ? "text-cyan-700 bg-cyan-50 border-cyan-200"
                : url
                ? "text-zinc-600 bg-zinc-100 border-zinc-200"
                : "text-zinc-500 bg-zinc-50 border-zinc-200"
            }`}
          >
            {url ? (active ? "Aktiv im POS" : "Deaktiviert") : "Kein Motiv"}
          </div>
        </div>

        {url ? (
          <div
            className="rounded-md overflow-hidden border border-zinc-200 bg-zinc-50"
            data-testid={`pos-ad-banner-preview-${slot}`}
          >
            <img
              src={url}
              alt=""
              draggable={false}
              className={`w-full h-24 sm:h-32 lg:h-40 object-cover object-center ${active ? "" : "opacity-40"}`}
            />
          </div>
        ) : (
          <button
            type="button"
            data-testid={`pos-ad-banner-preview-${slot}`}
            onClick={() => document.getElementById(inputId).click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            disabled={uploading}
            className={`w-full rounded-md border-2 border-dashed flex flex-col items-center justify-center gap-2 h-32 sm:h-36 lg:h-40 transition-colors cursor-pointer group ${
              dragOver
                ? "border-cyan-500 bg-cyan-50"
                : "border-zinc-300 bg-zinc-50 hover:border-cyan-400 hover:bg-cyan-50/40"
            }`}
          >
            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              dragOver ? "bg-cyan-100 text-cyan-700" : "bg-white text-zinc-500 group-hover:text-cyan-600 border border-zinc-200"
            }`}>
              <Upload size={18} />
            </div>
            <div className="text-sm font-medium text-zinc-800">
              {uploading ? "Lade hoch…" : "Bild hierher ziehen oder klicken"}
            </div>
            <div className="text-[11px] text-zinc-500">PNG · JPG · WebP · max. 1,5&nbsp;MB · empfohlen 1400 × 200&nbsp;px</div>
          </button>
        )}

        {fileInput}

        <div className="flex flex-wrap gap-2 items-center pt-1">
          <Button
            type="button"
            data-testid={`pos-ad-banner-upload-btn-${slot}`}
            className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 font-semibold shadow-sm"
            onClick={() => document.getElementById(inputId).click()}
            disabled={uploading}
          >
            <Upload size={14} className="mr-2" />
            {uploading ? "Lade hoch…" : (url ? "Motiv ersetzen" : "Bild hochladen")}
          </Button>
          {url && (
            <>
              <Button
                type="button"
                data-testid={active ? `pos-ad-banner-deactivate-btn-${slot}` : `pos-ad-banner-activate-btn-${slot}`}
                variant="outline"
                className={
                  active
                    ? "border-yellow-300 bg-yellow-50 text-yellow-700 hover:bg-yellow-100"
                    : "border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                }
                onClick={() => toggleActive(!active)}
              >
                {active ? "Deaktivieren" : "Aktivieren"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                data-testid={`pos-ad-banner-remove-btn-${slot}`}
                onClick={removeBanner}
              >
                <Trash2 size={14} className="mr-2" /> Entfernen
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // DARK THEME — UNVERÄNDERT (Feature Freeze, kein visueller Drift).
  // -------------------------------------------------------------------
  return (
    <div
      className="border border-zinc-800 rounded-sm bg-zinc-950/60 p-3 space-y-3"
      data-testid={`pos-ad-banner-slot-${slot}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-zinc-400">Banner {slot}</div>
        <div
          data-testid={`pos-ad-banner-status-${slot}`}
          className={`text-[11px] uppercase tracking-widest ${
            url && active ? "text-cyan-400" : "text-zinc-500"
          }`}
        >
          {url ? (active ? "Aktiv im POS" : "Deaktiviert") : "Kein Motiv"}
        </div>
      </div>

      <div
        className="rounded-sm overflow-hidden border border-zinc-800 bg-zinc-950 min-h-[6rem]"
        data-testid={`pos-ad-banner-preview-${slot}`}
      >
        {url ? (
          <img
            src={url}
            alt=""
            draggable={false}
            className={`w-full h-24 sm:h-32 lg:h-40 object-cover object-center ${active ? "" : "opacity-40"}`}
          />
        ) : (
          <div className="w-full h-24 sm:h-32 lg:h-40 border border-dashed border-zinc-700 flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
            Kein Bannerbild — hochladen, um Vorschau zu sehen
          </div>
        )}
      </div>

      {fileInput}
      <div className="flex flex-wrap gap-2 items-center">
        <Button
          type="button"
          data-testid={`pos-ad-banner-upload-btn-${slot}`}
          className="bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-cyan-400"
          onClick={() => document.getElementById(inputId).click()}
          disabled={uploading}
        >
          <Upload size={14} className="mr-2" />
          {uploading ? "Lade hoch…" : (url ? "Motiv ersetzen" : "Bild hochladen")}
        </Button>
        {url && (
          <>
            <Button
              type="button"
              data-testid={active ? `pos-ad-banner-deactivate-btn-${slot}` : `pos-ad-banner-activate-btn-${slot}`}
              variant="outline"
              className={active ? "border-yellow-500/40 text-yellow-400" : "border-cyan-500/40 text-cyan-400"}
              onClick={() => toggleActive(!active)}
            >
              {active ? "Deaktivieren" : "Aktivieren"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="text-rose-400"
              data-testid={`pos-ad-banner-remove-btn-${slot}`}
              onClick={removeBanner}
            >
              <Trash2 size={14} className="mr-2" /> Entfernen
            </Button>
          </>
        )}
      </div>
    </div>
  );
}


function AccentRow({ label, testIdPrefix, value, setValue, save }) {  return (
    <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-950/40 space-y-3 min-w-0">
      <Label className="text-xs uppercase tracking-wider text-zinc-400 break-words">{label}</Label>
      <div className="flex items-center gap-2 min-w-0">
        <input type="color" data-testid={`${testIdPrefix}-picker`}
               value={value || "#22D3EE"}
               onChange={(e) => setValue(e.target.value.toUpperCase())}
               className="h-10 w-12 rounded-sm border border-zinc-800 bg-zinc-950 cursor-pointer shrink-0" />
        <Input data-testid={`${testIdPrefix}-hex`}
               value={value} placeholder="#22D3EE"
               onChange={(e) => setValue(e.target.value)}
               maxLength={9}
               className="h-10 bg-zinc-950 border-zinc-800 rounded-sm font-mono min-w-0 flex-1" />
      </div>
      <div className="h-9 w-full px-3 rounded-sm inline-flex items-center justify-center"
           style={{ background: value || "#22D3EE", color: "#0a0a0a" }}
           data-testid={`${testIdPrefix}-preview`}>
        <span className="font-black uppercase tracking-wider text-xs">Vorschau</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid={`${testIdPrefix}-save-btn`} onClick={() => save.mutate()}
                className="bg-cyan-400 hover:bg-cyan-300 h-9 flex-1 sm:flex-none min-w-[7rem]">
          <Save size={13} className="mr-1 shrink-0" /> Übernehmen
        </Button>
        <Button data-testid={`${testIdPrefix}-reset-btn`} variant="ghost"
                onClick={() => { setValue(""); save.mutate(); }}
                className="text-zinc-400 h-9 flex-1 sm:flex-none whitespace-normal sm:whitespace-nowrap text-xs sm:text-sm px-2">
          Auf Standard zurücksetzen
        </Button>
      </div>
    </div>
  );
}
